/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterViewInit,
    Component,
    ElementRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { HotKeyService } from '../../../../core/ui/hotkeys/hot-key.service';
import { MapCommand } from '../../command/map-command.class';
import { LoggerService } from '../../../shared/services/logger.service';
import { takeUntil } from 'rxjs/operators';
import { WidgetCustomUiDirective } from './widget-custom-ui.directive';
import { MapCommandHost } from '../../command/map-command-host.class';
import { MatIconButton } from '@angular/material/button';
import { ActiveToolDirective } from '../active-tool/active-tool.directive';
import { MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'dash-gis-map-toolbar-widget',
    templateUrl: './map-toolbar-widget.component.html',
    styleUrls: ['map-toolbar-widget.component.scss'],
    imports: [
        WidgetCustomUiDirective,
        MatIconButton,
        ActiveToolDirective,
        MatMenuTrigger,
        MatTooltip,
        MatIcon,
        MatMenu,
        MatMenuContent,
        MatMenuItem,
        AsyncPipe,
    ],
})
export class MapToolbarWidgetComponent extends MapCommandHost implements OnChanges, AfterViewInit, OnDestroy {
    @Input() hotKey: string;
    @Input() label: string;
    @Input() commandName: string;
    @Input() icon: string;
    @Input() isInBottomSheet: boolean;
    @Input() attributes: { [key: string]: string };
    @Output() shouldPreventClose = new BehaviorSubject<boolean>(false);

    @ViewChild(WidgetCustomUiDirective, { static: false }) customUi: WidgetCustomUiDirective;

    protected command: MapCommand;
    protected dispose$: Observable<void>;

    private logger = inject(LoggerService);
    private hotkey = inject(HotKeyService);
    private element = inject(ElementRef);

    private disposeSubject = new Subject<void>();

    constructor() {
        super();
        this.dispose$ = this.disposeSubject.asObservable();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.commandName?.currentValue) {
            this.command = this.setupCommand(changes.commandName.currentValue, changes?.attributes?.currentValue);
            this.command.preventClose$
                .pipe(takeUntil(this.dispose$))
                .subscribe((preventClose) => this.shouldPreventClose.next(preventClose));
        }
    }

    ngAfterViewInit() {
        if (!!this.hotkey) {
            this.hotkey.addCallback(this.hotKey, () => {
                const buttons = (this.element.nativeElement as HTMLElement).getElementsByTagName('button');
                if (buttons.length > 0) {
                    buttons[0].click();
                }
            });
        }
        if (this.command?.hasCustomUi()) {
            this.loadCustomUi();
        }
    }

    ngOnDestroy() {
        this.disposeSubject.next();
        this.disposeSubject.unsubscribe();
    }

    invoke() {
        this.command.invoke();
    }

    toolDeactivated() {
        this.command.deactivated$.next();
    }

    /**
     * If the reason the menu was closed is unknown or keydown, it
     * was closed without choosing an item (i.e., cancelled)
     * @param reason
     */
    menuClosed(reason: any) {
        if (reason === undefined || reason === 'keydown') {
            this.localDeactivate$.next();
        }
    }

    loadCustomUi() {
        const uiType = this.command.getCustomUiType();
        this.command.createCustomUi(this.customUi.viewContainerRef, uiType);
    }

    checkAuthorization$(): Observable<boolean> {
        return of(true);
    }
}
